export interface Question {
  id: number;
  text: string;
  options: string[];
  correctAnswers: number[];
  level: 'easy' | 'medium' | 'experienced';
  marks: number;
  explanation: string;
}

export const questions: Question[] = [
  {
    id: 1,
    text: "Which of the following are valid C# access modifiers?",
    options: ["private", "protected", "internal", "external"],
    correctAnswers: [0, 1, 2],
    level: "easy",
    marks: 2,
    explanation: "C# has private, protected, internal, public, and protected internal access modifiers. 'external' is not valid."
  },
  {
    id: 2,
    text: "What is the correct way to implement IDisposable?",
    options: [
      "Just implement the Dispose method",
      "Implement Dispose and a finalizer",
      "Implement the dispose pattern with a finalizer and protected Dispose(bool)",
      "Only implement a finalizer"
    ],
    correctAnswers: [2],
    level: "experienced",
    marks: 3,
    explanation: "The proper implementation includes both Dispose methods and a finalizer for proper resource cleanup."
  },
  {
    id: 3,
    text: "Which statements about .NET Core are true?",
    options: [
      "It's cross-platform",
      "It's open-source",
      "It only runs on Windows",
      "It's slower than .NET Framework"
    ],
    correctAnswers: [0, 1],
    level: "medium",
    marks: 2,
    explanation: ".NET Core is both cross-platform and open-source."
  },
  {
    id: 4,
    text: "What is the purpose of the 'using' statement in C#?",
    options: [
      "To include namespaces",
      "To manage resource disposal",
      "To create aliases for namespaces",
      "To handle exceptions"
    ],
    correctAnswers: [1],
    level: "easy",
    marks: 1,
    explanation: "The 'using' statement ensures that Dispose is called on objects when they are no longer needed."
  },
  {
    id: 5,
    text: "Which of the following are reference types in C#?",
    options: ["int", "string", "struct", "class"],
    correctAnswers: [1, 3],
    level: "easy",
    marks: 2,
    explanation: "'string' and 'class' are reference types; 'int' and 'struct' are value types."
  },
  {
    id: 6,
    text: "What is the output of the following code?\n\n```csharp\nint x = 5;\nConsole.WriteLine(x++);\n```",
    options: ["5", "6", "Error", "Undefined"],
    correctAnswers: [0],
    level: "easy",
    marks: 1,
    explanation: "The post-increment operator returns the value before incrementing."
  },
  {
    id: 7,
    text: "In C#, what is 'boxing'?",
    options: [
      "Converting a value type to a reference type",
      "Converting a reference type to a value type",
      "Wrapping a method in a delegate",
      "Encapsulating classes"
    ],
    correctAnswers: [0],
    level: "medium",
    marks: 2,
    explanation: "Boxing is converting a value type to a reference type by wrapping it in an object."
  },
  {
    id: 8,
    text: "Which keyword is used to prevent a class from being inherited?",
    options: ["sealed", "static", "abstract", "virtual"],
    correctAnswers: [0],
    level: "easy",
    marks: 1,
    explanation: "The 'sealed' keyword prevents inheritance of a class."
  },
  {
    id: 9,
    text: "What is the difference between an abstract class and an interface in C#?",
    options: [
      "Interfaces can have implementations; abstract classes cannot",
      "Abstract classes can have implementations; interfaces cannot",
      "Both can have implementations",
      "Neither can have implementations"
    ],
    correctAnswers: [1],
    level: "medium",
    marks: 2,
    explanation: "Abstract classes can have implementations; traditional interfaces cannot."
  },
  {
    id: 10,
    text: "What does the 'async' keyword indicate in a method signature?",
    options: [
      "The method runs on a separate thread",
      "The method contains asynchronous operations",
      "The method is automatically parallelized",
      "The method returns immediately"
    ],
    correctAnswers: [1],
    level: "medium",
    marks: 2,
    explanation: "'async' indicates that the method contains awaitable asynchronous operations."
  },
  // Questions 11 to 100 would follow the same format...
  {
    id: 11,
    text: "Which LINQ method filters a sequence based on a predicate?",
    options: ["Select", "Where", "OrderBy", "GroupBy"],
    correctAnswers: [1],
    level: "easy",
    marks: 1,
    explanation: "'Where' filters elements based on a predicate."
  },
  {
    id: 12,
    text: "In C#, what is the purpose of the 'yield' keyword?",
    options: [
      "To pause and resume methods",
      "To return multiple values from a method",
      "To create an iterator block",
      "To generate asynchronous methods"
    ],
    correctAnswers: [2],
    level: "experienced",
    marks: 3,
    explanation: "'yield' is used in iterator methods to provide a custom iteration over a collection."
  },
  {
    id: 13,
    text: "What is a 'delegate' in C#?",
    options: [
      "A type that represents references to methods",
      "A placeholder for a value",
      "An access modifier",
      "A type of exception"
    ],
    correctAnswers: [0],
    level: "medium",
    marks: 2,
    explanation: "Delegates are types that represent references to methods with a specific parameter list and return type."
  },
  {
    id: 14,
    text: "Which of the following are value types in C#?",
    options: ["int", "enum", "class", "interface"],
    correctAnswers: [0, 1],
    level: "easy",
    marks: 2,
    explanation: "'int' and 'enum' are value types; 'class' and 'interface' are reference types."
  },
  {
    id: 15,
    text: "What does the 'virtual' keyword indicate in a method declaration?",
    options: [
      "The method is abstract",
      "The method can be overridden",
      "The method is static",
      "The method is sealed"
    ],
    correctAnswers: [1],
    level: "medium",
    marks: 2,
    explanation: "'virtual' allows a method to be overridden in derived classes."
  },
  {
    id: 16,
    text: "What is the purpose of the 'lock' statement in C#?",
    options: [
      "To prevent a class from being inherited",
      "To control access to a block of code by multiple threads",
      "To encrypt data",
      "To create a singleton"
    ],
    correctAnswers: [1],
    level: "experienced",
    marks: 3,
    explanation: "'lock' ensures that one thread at a time executes a block of code."
  },
  {
    id: 17,
    text: "Which exception is thrown when an array index is out of bounds?",
    options: [
      "IndexOutOfRangeException",
      "NullReferenceException",
      "ArgumentException",
      "InvalidOperationException"
    ],
    correctAnswers: [0],
    level: "easy",
    marks: 1,
    explanation: "Accessing an invalid array index throws IndexOutOfRangeException."
  },
  {
    id: 18,
    text: "What is the output of the following code?\n\n```csharp\nvar list = new List<int> {1, 2, 3};\nlist.Insert(1, 5);\nConsole.WriteLine(string.Join(\",\", list));\n```",
    options: ["1,5,2,3", "1,2,3,5", "5,1,2,3", "1,2,3"],
    correctAnswers: [0],
    level: "medium",
    marks: 2,
    explanation: "Insert adds an element at the specified index; existing elements shift right."
  },
  {
    id: 19,
    text: "In Entity Framework, what does lazy loading mean?",
    options: [
      "Loading related data immediately",
      "Deferring the loading of related data until it is accessed",
      "Loading all data at application start",
      "Preloading data into cache"
    ],
    correctAnswers: [1],
    level: "experienced",
    marks: 3,
    explanation: "Lazy loading delays the loading of related data until it's actually needed."
  },
  {
    id: 20,
    text: "Which of the following are features of object-oriented programming?",
    options: ["Encapsulation", "Polymorphism", "Recursion", "Abstraction"],
    correctAnswers: [0, 1, 3],
    level: "easy",
    marks: 2,
    explanation: "Encapsulation, polymorphism, and abstraction are core OOP concepts."
  },
  // Continue adding questions up to id: 100
  {
    id: 21,
    text: "What does the 'static' keyword mean in C#?",
    options: [
      "The member belongs to the type itself rather than to a specific object",
      "The member cannot be modified",
      "The member is accessible only within its own class",
      "The member is inherited"
    ],
    correctAnswers: [0],
    level: "easy",
    marks: 1,
    explanation: "'static' indicates that the member belongs to the type itself."
  },
  {
    id: 22,
    text: "What is the purpose of the 'finally' block in exception handling?",
    options: [
      "To catch exceptions not handled by 'catch' blocks",
      "To execute code regardless of whether an exception is thrown",
      "To rethrow caught exceptions",
      "To terminate the program"
    ],
    correctAnswers: [1],
    level: "medium",
    marks: 2,
    explanation: "'finally' executes code after try/catch, regardless of an exception."
  },
  {
    id: 23,
    text: "Which collection guarantees uniqueness of its elements?",
    options: ["List", "Dictionary", "HashSet", "Queue"],
    correctAnswers: [2],
    level: "easy",
    marks: 1,
    explanation: "HashSet does not allow duplicate elements."
  },
  {
    id: 24,
    text: "What is the difference between 'String' and 'StringBuilder'?",
    options: [
      "'String' is mutable; 'StringBuilder' is immutable",
      "'String' is immutable; 'StringBuilder' is mutable",
      "Both are mutable",
      "Both are immutable"
    ],
    correctAnswers: [1],
    level: "medium",
    marks: 2,
    explanation: "'String' is immutable; 'StringBuilder' is designed for mutable string manipulation."
  },
  {
    id: 25,
    text: "In C#, which operator is used for null-coalescing?",
    options: ["??", "?.", "??=", "?:"],
    correctAnswers: [0],
    level: "medium",
    marks: 2,
    explanation: "The '??' operator returns the left-hand operand if it's not null."
  },
  {
    id: 26,
    text: "What is a 'Tuple' in C#?",
    options: [
      "A fixed-size collection of elements",
      "A method that returns multiple values",
      "A data structure that holds a sequence of elements",
      "An anonymous type with ordered elements"
    ],
    correctAnswers: [3],
    level: "easy",
    marks: 1,
    explanation: "Tuples are types that can hold multiple values of different types."
  },
  {
    id: 27,
    text: "What is the purpose of attributes in C#?",
    options: [
      "To add metadata to code elements",
      "To define properties",
      "To handle events",
      "To enforce security"
    ],
    correctAnswers: [0],
    level: "experienced",
    marks: 3,
    explanation: "Attributes add metadata that can be inspected at runtime or compile-time."
  },
  {
    id: 28,
    text: "Which of the following are valid generic type constraints?",
    options: ["where T : class", "where T : struct", "where T : new()", "where T : static"],
    correctAnswers: [0, 1, 2],
    level: "medium",
    marks: 2,
    explanation: "Valid constraints are 'class', 'struct', and 'new()'; 'static' is not valid."
  },
  {
    id: 29,
    text: "What is the use of the 'params' keyword in method parameters?",
    options: [
      "To pass a variable number of arguments",
      "To pass parameters by reference",
      "To specify optional parameters",
      "To enforce type safety"
    ],
    correctAnswers: [0],
    level: "medium",
    marks: 2,
    explanation: "'params' allows passing an array or a variable number of arguments."
  },
  {
    id: 30,
    text: "Which class provides methods for generating random numbers?",
    options: ["Math", "Random", "RNGCryptoServiceProvider", "NumberGenerator"],
    correctAnswers: [1],
    level: "easy",
    marks: 1,
    explanation: "The 'Random' class is used to generate pseudo-random numbers."
  },
  {
    id: 31,
    text: "In C#, which access modifier makes a member visible only within its own assembly?",
    options: ["private", "protected", "internal", "public"],
    correctAnswers: [2],
    level: "medium",
    marks: 2,
    explanation: "'internal' limits access to the current assembly."
  },
  {
    id: 32,
    text: "What does 'T' represent in a generic class declaration 'class MyClass<T>'?",
    options: [
      "A type parameter",
      "An object instance",
      "A namespace",
      "A method parameter"
    ],
    correctAnswers: [0],
    level: "easy",
    marks: 1,
    explanation: "'T' is a type parameter in a generic class."
  },
  {
    id: 33,
    text: "What is the purpose of 'String.Format' method?",
    options: [
      "To concatenate strings",
      "To insert objects into a string",
      "To compare strings",
      "To encode strings"
    ],
    correctAnswers: [1],
    level: "easy",
    marks: 1,
    explanation: "'String.Format' formats strings by replacing placeholders with values."
  },
  {
    id: 34,
    text: "Which of the following are characteristics of a stack data structure?",
    options: [
      "First In First Out",
      "Last In First Out",
      "Supports Push and Pop operations",
      "Elements can be accessed randomly"
    ],
    correctAnswers: [1, 2],
    level: "medium",
    marks: 2,
    explanation: "A stack is LIFO and supports Push and Pop; elements are not randomly accessible."
  },
  {
    id: 35,
    text: "What is the difference between 'throw' and 'throw ex' in exception handling?",
    options: [
      "'throw' preserves the original stack trace",
      "'throw ex' preserves the original stack trace",
      "No difference",
      "'throw ex' rethrows the exception as is"
    ],
    correctAnswers: [0],
    level: "experienced",
    marks: 3,
    explanation: "'throw' rethrows preserving the stack trace; 'throw ex' resets it."
  },
  {
    id: 36,
    text: "Which keyword is used to inherit from a base class in C#?",
    options: ["inherits", "extends", ":", "->"],
    correctAnswers: [2],
    level: "easy",
    marks: 1,
    explanation: "':' is used in the class declaration to inherit from a base class."
  },
  {
    id: 37,
    text: "In C#, what is 'Reflection' used for?",
    options: [
      "Managing memory",
      "Inspecting metadata at runtime",
      "Handling exceptions",
      "Optimizing performance"
    ],
    correctAnswers: [1],
    level: "experienced",
    marks: 3,
    explanation: "Reflection allows inspection of metadata and types at runtime."
  },
  {
    id: 38,
    text: "What is the output of the following code?\n\n```csharp\nstring s = null;\nConsole.WriteLine(s?.Length ?? 0);\n```",
    options: ["0", "NullReferenceException", "Compile Error", "Undefined"],
    correctAnswers: [0],
    level: "medium",
    marks: 2,
    explanation: "The null-conditional operator returns null; '??' provides the default value."
  },
  {
    id: 39,
    text: "Which of the following are immutable in C#?",
    options: ["String", "DateTime", "StringBuilder", "List"],
    correctAnswers: [0, 1],
    level: "medium",
    marks: 2,
    explanation: "'String' and 'DateTime' are immutable; 'StringBuilder' and 'List' are mutable."
  },
  {
    id: 40,
    text: "What is the default access modifier for class members in C#?",
    options: ["private", "protected", "internal", "public"],
    correctAnswers: [0],
    level: "easy",
    marks: 1,
    explanation: "Class members are private by default."
  },
  // Continue adding questions up to id: 100
  // Skipping ahead for brevity
  {
    id: 100,
    text: "What is the main purpose of the IDisposable interface?",
    options: [
      "To provide a method for releasing unmanaged resources",
      "To enforce garbage collection",
      "To manage memory allocation",
      "To handle exceptions"
    ],
    correctAnswers: [0],
    level: "medium",
    marks: 2,
    explanation: "IDisposable defines a standard method for releasing unmanaged resources."
  },
  {
    id: 41,
    text: "What is the purpose of the 'var' keyword in C# 3.0 and later?",
    options: [
      "To declare a variable with dynamic typing",
      "To enable implicit typing of local variables",
      "To declare a variant data type",
      "To define global variables"
    ],
    correctAnswers: [1],
    level: "easy",
    marks: 1,
    explanation: "The 'var' keyword allows the compiler to infer the type of the variable from the assigned expression."
  },
  {
    id: 42,
    text: "Which interface must be implemented to create a custom collection that can be enumerated using 'foreach'?",
    options: ["IEnumerable", "ICollection", "IEnumerator", "IDisposable"],
    correctAnswers: [0],
    level: "medium",
    marks: 2,
    explanation: "Implementing IEnumerable allows a collection to be enumerated using 'foreach'."
  },
  {
    id: 43,
    text: "What is the main advantage of using 'StringBuilder' over 'String' for string manipulation?",
    options: [
      "StringBuilder is immutable",
      "StringBuilder has better performance for frequent modifications",
      "StringBuilder uses less memory",
      "StringBuilder supports more methods"
    ],
    correctAnswers: [1],
    level: "medium",
    marks: 2,
    explanation: "StringBuilder is mutable and is optimized for scenarios where strings are modified frequently."
  },
  {
    id: 44,
    text: "In asynchronous programming, what does the 'await' keyword do?",
    options: [
      "It pauses the method until the awaited task completes",
      "It starts a new task",
      "It blocks the current thread",
      "It ignores exceptions in asynchronous methods"
    ],
    correctAnswers: [0],
    level: "medium",
    marks: 2,
    explanation: "'await' pauses the async method until the awaited task completes without blocking the thread."
  },
  {
    id: 45,
    text: "What is the purpose of 'Task.Run' in asynchronous programming?",
    options: [
      "To execute CPU-bound work on a background thread",
      "To start an I/O-bound asynchronous operation",
      "To create a new process",
      "To synchronize threads"
    ],
    correctAnswers: [0],
    level: "experienced",
    marks: 3,
    explanation: "'Task.Run' queues work to run on a ThreadPool thread, suitable for CPU-bound operations."
  },
  {
    id: 46,
    text: "Which of the following are features of LINQ (Language Integrated Query)?",
    options: [
      "Deferred execution",
      "Strongly typed queries",
      "Integration with various data sources",
      "Runtime query compilation"
    ],
    correctAnswers: [0, 1, 2],
    level: "medium",
    marks: 2,
    explanation: "LINQ provides deferred execution, strong typing, and can query different data sources."
  },
  {
    id: 47,
    text: "What does the 'dynamic' keyword signify in C#?",
    options: [
      "Static typing",
      "Runtime type checking",
      "Compile-time type checking",
      "An alias for 'object'"
    ],
    correctAnswers: [1],
    level: "experienced",
    marks: 3,
    explanation: "'dynamic' tells the compiler to defer type checking until runtime."
  },
  {
    id: 48,
    text: "Which keyword is used to define an indexer in C#?",
    options: ["this", "base", "operator", "value"],
    correctAnswers: [0],
    level: "medium",
    marks: 2,
    explanation: "An indexer is defined using the 'this' keyword with square brackets."
  },
  {
    id: 49,
    text: "What is the output of the following code?\n\n```csharp\nConsole.WriteLine(0.1 + 0.2 == 0.3);\n```",
    options: ["True", "False", "Compilation Error", "Exception"],
    correctAnswers: [1],
    level: "experienced",
    marks: 3,
    explanation: "Due to floating-point precision errors, 0.1 + 0.2 does not exactly equal 0.3."
  },
  {
    id: 50,
    text: "In C#, events are based on which of the following?",
    options: ["Delegates", "Interfaces", "Attributes", "Generics"],
    correctAnswers: [0],
    level: "medium",
    marks: 2,
    explanation: "Events in C# are built upon delegates."
  },
  {
    id: 51,
    text: "Which of the following statements about the 'abstract' keyword are true?",
    options: [
      "An abstract class cannot be instantiated",
      "An abstract method must be implemented in derived classes",
      "An abstract class can contain non-abstract methods",
      "Abstract methods can have a body"
    ],
    correctAnswers: [0, 1, 2],
    level: "medium",
    marks: 2,
    explanation: "Abstract classes cannot be instantiated, can contain both abstract and non-abstract methods, and abstract methods must be overridden."
  },
  {
    id: 52,
    text: "What is the purpose of the 'using System;' statement at the beginning of a C# file?",
    options: [
      "To include the System library for use",
      "To declare a namespace",
      "To manage resource disposal",
      "To import classes from the System namespace"
    ],
    correctAnswers: [3],
    level: "easy",
    marks: 1,
    explanation: "It allows the use of classes in the System namespace without fully qualifying them."
  },
  {
    id: 53,
    text: "What is a 'namespace' used for in C#?",
    options: [
      "To group related classes and interfaces",
      "To control access levels",
      "To manage memory",
      "To handle exceptions"
    ],
    correctAnswers: [0],
    level: "easy",
    marks: 1,
    explanation: "Namespaces organize code and prevent naming conflicts."
  },
  {
    id: 54,
    text: "Which of the following are thread synchronization primitives in .NET?",
    options: ["lock", "Mutex", "Semaphore", "Barrier"],
    correctAnswers: [0, 1, 2, 3],
    level: "experienced",
    marks: 3,
    explanation: "All listed options are synchronization primitives available in .NET."
  },
  {
    id: 55,
    text: "What is the purpose of the 'partial' keyword in class definitions?",
    options: [
      "To define a class that can be split across multiple files",
      "To define an abstract class",
      "To declare a class that is only partially implemented",
      "To indicate inheritance from multiple classes"
    ],
    correctAnswers: [0],
    level: "medium",
    marks: 2,
    explanation: "'partial' allows a class to be defined in multiple files."
  },
  {
    id: 56,
    text: "Which method is used to start a new thread in C#?",
    options: ["Thread.Run()", "Thread.Execute()", "Thread.Start()", "Thread.Begin()"],
    correctAnswers: [2],
    level: "medium",
    marks: 2,
    explanation: "The 'Start()' method begins thread execution."
  },
  {
    id: 57,
    text: "In ASP.NET Core, which middleware component is responsible for routing?",
    options: ["UseEndpoints", "UseRouting", "UseAuthentication", "UseMvc"],
    correctAnswers: [1],
    level: "experienced",
    marks: 3,
    explanation: "The 'UseRouting' middleware matches request URLs to endpoints."
  },
  {
    id: 58,
    text: "What is the difference between 'IEnumerable' and 'IQueryable'?",
    options: [
      "IEnumerable executes queries in-memory; IQueryable can execute on remote data sources",
      "IQueryable executes queries in-memory; IEnumerable can execute on remote data sources",
      "IEnumerable supports deferred execution; IQueryable does not",
      "IQueryable supports LINQ queries; IEnumerable does not"
    ],
    correctAnswers: [0],
    level: "experienced",
    marks: 3,
    explanation: "IQueryable allows for remote query execution, such as in databases."
  },
  {
    id: 59,
    text: "Which of the following are true about 'async' methods in C#?",
    options: [
      "They must return Task or Task<T>",
      "They can have 'void' return type",
      "They can use the 'await' keyword",
      "They run synchronously"
    ],
    correctAnswers: [0, 1, 2],
    level: "medium",
    marks: 2,
    explanation: "Async methods typically return Task or Task<T>, can return void for event handlers, and use 'await'."
  },
  {
    id: 60,
    text: "What is the purpose of 'GC.Collect()' in .NET?",
    options: [
      "To force immediate garbage collection",
      "To free unmanaged resources",
      "To prevent garbage collection",
      "To collect diagnostic data"
    ],
    correctAnswers: [0],
    level: "experienced",
    marks: 3,
    explanation: "Calling 'GC.Collect()' forces the garbage collector to run."
  },
  {
    id: 61,
    text: "In C#, what does the 'params' keyword allow you to do?",
    options: [
      "Pass an array of arguments",
      "Pass arguments by reference",
      "Pass a variable number of arguments",
      "Pass parameters with default values"
    ],
    correctAnswers: [2],
    level: "medium",
    marks: 2,
    explanation: "'params' allows a method to accept a variable number of parameters."
  },
  {
    id: 62,
    text: "What is the use of the 'nameof' operator introduced in C# 6.0?",
    options: [
      "To get the name of a variable as a string",
      "To compare variable names",
      "To check if a variable exists",
      "To create anonymous types"
    ],
    correctAnswers: [0],
    level: "medium",
    marks: 2,
    explanation: "'nameof' returns the name of a variable, type, or member as a string."
  },
  {
    id: 63,
    text: "Which of the following are .NET Core CLI commands?",
    options: ["dotnet run", "dotnet build", "dotnet publish", "dotnet execute"],
    correctAnswers: [0, 1, 2],
    level: "easy",
    marks: 1,
    explanation: "'dotnet run', 'dotnet build', and 'dotnet publish' are valid commands; 'dotnet execute' is not."
  },
  {
    id: 64,
    text: "What is the purpose of 'Dependency Injection' in ASP.NET Core?",
    options: [
      "To manage object creation and lifetime",
      "To inject code at runtime",
      "To handle exceptions",
      "To improve application security"
    ],
    correctAnswers: [0],
    level: "experienced",
    marks: 3,
    explanation: "Dependency Injection allows for better management of dependencies and promotes loose coupling."
  },
  {
    id: 65,
    text: "Which keyword is used to define a read-only property in C#?",
    options: ["const", "readonly", "get", "set"],
    correctAnswers: [2],
    level: "easy",
    marks: 1,
    explanation: "A property with only a 'get' accessor is read-only."
  },
  {
    id: 66,
    text: "What is 'async/await' primarily used for in C#?",
    options: [
      "Parallel programming",
      "Asynchronous programming",
      "Real-time data processing",
      "Event handling"
    ],
    correctAnswers: [1],
    level: "medium",
    marks: 2,
    explanation: "'async/await' simplifies asynchronous programming by allowing code to be written in a synchronous style."
  },
  {
    id: 67,
    text: "Which collection is best suited for FIFO (First-In, First-Out) operations?",
    options: ["Stack", "Queue", "List", "Dictionary"],
    correctAnswers: [1],
    level: "easy",
    marks: 1,
    explanation: "A 'Queue' is designed for FIFO operations."
  },
  {
    id: 68,
    text: "What is the purpose of the 'sealed' keyword when applied to a method?",
    options: [
      "To prevent the method from being overridden in derived classes",
      "To make the method static",
      "To hide the method from derived classes",
      "To make the method abstract"
    ],
    correctAnswers: [0],
    level: "medium",
    marks: 2,
    explanation: "A 'sealed' method cannot be overridden further in any subclass."
  },
  {
    id: 69,
    text: "In C#, which of the following types can be used as nullable types?",
    options: ["int", "double", "bool", "string"],
    correctAnswers: [0, 1, 2],
    level: "easy",
    marks: 1,
    explanation: "Value types like int, double, and bool can be made nullable using '?'. Reference types like string are already nullable."
  },
  {
    id: 70,
    text: "What is 'boxing' in C#?",
    options: [
      "Converting a reference type to a value type",
      "Converting a value type to a reference type",
      "Copying a value type",
      "Encapsulating an object"
    ],
    correctAnswers: [1],
    level: "medium",
    marks: 2,
    explanation: "Boxing wraps a value type instance inside an object, converting it to a reference type."
  },
  {
    id: 71,
    text: "What is the default value of a reference type in C#?",
    options: ["null", "0", "An empty object", "Undefined"],
    correctAnswers: [0],
    level: "easy",
    marks: 1,
    explanation: "Reference types default to 'null' if not initialized."
  },
  {
    id: 72,
    text: "Which method is used to sort a list in C#?",
    options: ["Sort()", "Order()", "Arrange()", "Sequence()"],
    correctAnswers: [0],
    level: "easy",
    marks: 1,
    explanation: "The 'Sort()' method sorts the elements in a List."
  },
  {
    id: 73,
    text: "What is an extension method in C#?",
    options: [
      "A method that extends the functionality of an existing type",
      "A method that is inherited from a base class",
      "A method that overrides a virtual method",
      "A method that is marked as obsolete"
    ],
    correctAnswers: [0],
    level: "experienced",
    marks: 3,
    explanation: "Extension methods allow adding new methods to existing types without modifying them."
  },
  {
    id: 74,
    text: "Which of the following are valid reasons to use properties instead of public fields?",
    options: [
      "To provide encapsulation",
      "To validate data before setting",
      "To control accessibility",
      "To improve performance"
    ],
    correctAnswers: [0, 1, 2],
    level: "medium",
    marks: 2,
    explanation: "Properties allow encapsulation, validation, and control over access."
  },
  {
    id: 75,
    text: "What is the 'out' keyword used for in method parameters?",
    options: [
      "To pass arguments by reference",
      "To indicate that the argument is optional",
      "To return multiple values from a method",
      "To specify an output parameter that must be assigned"
    ],
    correctAnswers: [3],
    level: "medium",
    marks: 2,
    explanation: "'out' parameters must be assigned within the method and allow methods to return multiple values."
  },
  {
    id: 76,
    text: "Which of the following is not a built-in delegate type in C#?",
    options: ["Func", "Action", "Predicate", "Handler"],
    correctAnswers: [3],
    level: "experienced",
    marks: 3,
    explanation: "'Handler' is not a built-in delegate; 'Func', 'Action', and 'Predicate' are built-in delegates."
  },
  {
    id: 77,
    text: "What is the purpose of the 'base' keyword in C#?",
    options: [
      "To refer to the current instance",
      "To refer to the base class constructor",
      "To access members of the base class",
      "To define a base class"
    ],
    correctAnswers: [1, 2],
    level: "medium",
    marks: 2,
    explanation: "'base' is used to call base class constructors and access base class members."
  },
  {
    id: 78,
    text: "In C#, which of the following statements about interfaces is true?",
    options: [
      "Interfaces can contain fields",
      "Interfaces can contain method implementations",
      "Interfaces support multiple inheritance",
      "Interfaces can be instantiated"
    ],
    correctAnswers: [2],
    level: "medium",
    marks: 2,
    explanation: "A class can implement multiple interfaces, thus interfaces support multiple inheritance."
  },
  {
    id: 79,
    text: "What is the output of the following code?\n\n```csharp\nstring s = \"Hello\";\ns += ' ';\ns += \"World\";\nConsole.WriteLine(s.Length);\n```",
    options: ["10", "11", "5", "6"],
    correctAnswers: [1],
    level: "easy",
    marks: 1,
    explanation: "The final string is \"Hello World\" which has 11 characters."
  },
  {
    id: 80,
    text: "Which keyword is used to prevent a method from being overridden in derived classes?",
    options: ["sealed", "static", "final", "override"],
    correctAnswers: [0],
    level: "medium",
    marks: 2,
    explanation: "The 'sealed' keyword prevents further overriding."
  },
  {
    id: 81,
    text: "What is the purpose of the 'IS' operator in C#?",
    options: [
      "To check if an object is of a certain type",
      "To cast an object to a different type",
      "To compare two objects for equality",
      "To create an instance of a type"
    ],
    correctAnswers: [0],
    level: "easy",
    marks: 1,
    explanation: "The 'is' operator checks if an object is compatible with a given type."
  },
  {
    id: 82,
    text: "Which of the following are correct ways to handle exceptions in C#?",
    options: [
      "Using try-catch blocks",
      "Using throw statements",
      "Using event handlers",
      "Using conditional statements"
    ],
    correctAnswers: [0, 1],
    level: "easy",
    marks: 1,
    explanation: "Exceptions are handled using try-catch blocks and can be thrown using 'throw'."
  },
  {
    id: 83,
    text: "What is the purpose of 'Enumerable.Select' in LINQ?",
    options: [
      "To filter a sequence based on a predicate",
      "To project each element into a new form",
      "To sort elements",
      "To group elements"
    ],
    correctAnswers: [1],
    level: "medium",
    marks: 2,
    explanation: "'Select' projects each element of a sequence into a new form."
  },
  {
    id: 84,
    text: "What does the 'volatile' keyword indicate in C#?",
    options: [
      "That a field may be modified by multiple threads",
      "That a variable cannot be optimized by the compiler",
      "That a field is constant",
      "That a variable is read-only"
    ],
    correctAnswers: [0],
    level: "experienced",
    marks: 3,
    explanation: "'volatile' ensures that the field is always read from and written to main memory."
  },
  {
    id: 85,
    text: "Which exception is thrown when a null reference is dereferenced?",
    options: [
      "NullReferenceException",
      "ArgumentNullException",
      "InvalidOperationException",
      "FormatException"
    ],
    correctAnswers: [0],
    level: "easy",
    marks: 1,
    explanation: "Accessing a member on a null reference throws NullReferenceException."
  },
  {
    id: 86,
    text: "What is 'method overloading' in C#?",
    options: [
      "Defining multiple methods with the same name but different parameters",
      "Defining a method in a derived class that overrides a base class method",
      "Increasing the performance of a method",
      "Calling a method recursively"
    ],
    correctAnswers: [0],
    level: "easy",
    marks: 1,
    explanation: "Method overloading allows methods with the same name to have different signatures."
  },
  {
    id: 87,
    text: "What is 'Type Inference' in C#?",
    options: [
      "Automatically determining the type of a variable",
      "Casting a variable to a different type",
      "Determining the base type of an object",
      "Inferring types during runtime"
    ],
    correctAnswers: [0],
    level: "medium",
    marks: 2,
    explanation: "Type inference allows the compiler to deduce the type from the assigned value."
  },
  {
    id: 88,
    text: "What does 'CLR' stand for in the context of .NET?",
    options: [
      "Common Language Runtime",
      "Central Logic Repository",
      "Code Language Resource",
      "Compile Link Run"
    ],
    correctAnswers: [0],
    level: "easy",
    marks: 1,
    explanation: "CLR stands for Common Language Runtime, the virtual machine component of .NET."
  },
  {
    id: 89,
    text: "Which of the following are benefits of exception handling?",
    options: [
      "Improved code readability",
      "Centralized error handling",
      "Program stability",
      "Increased performance"
    ],
    correctAnswers: [0, 1, 2],
    level: "medium",
    marks: 2,
    explanation: "Exception handling improves code readability, allows centralized error handling, and enhances program stability."
  },
  {
    id: 90,
    text: "In the context of design patterns, what does 'SOLID' stand for?",
    options: [
      "Single Responsibility Principle",
      "Open/Closed Principle",
      "Liskov Substitution Principle",
      "Interface Segregation Principle"
    ],
    correctAnswers: [0, 1, 2, 3],
    level: "experienced",
    marks: 3,
    explanation: "SOLID is an acronym for five design principles intended to make software designs more understandable."
  },
  {
    id: 91,
    text: "What is the main purpose of the 'Decorator' design pattern?",
    options: [
      "To add responsibilities to objects dynamically",
      "To create a family of related objects",
      "To define a one-to-many dependency",
      "To separate the construction of a complex object"
    ],
    correctAnswers: [0],
    level: "experienced",
    marks: 3,
    explanation: "The Decorator pattern adds new functionality to an existing object without altering its structure."
  },
  {
    id: 92,
    text: "Which of the following are considered value types in C#?",
    options: ["int", "decimal", "bool", "object"],
    correctAnswers: [0, 1, 2],
    level: "easy",
    marks: 1,
    explanation: "'int', 'decimal', and 'bool' are value types; 'object' is a reference type."
  },
  {
    id: 93,
    text: "What does 'Synchronous' mean in programming?",
    options: [
      "Operations happen sequentially",
      "Operations happen concurrently",
      "Operations are event-driven",
      "Operations are delayed"
    ],
    correctAnswers: [0],
    level: "easy",
    marks: 1,
    explanation: "Synchronous operations are performed one after the other."
  },
  {
    id: 94,
    text: "Which of the following are characteristics of the 'Singleton' design pattern?",
    options: [
      "Ensures only one instance of a class exists",
      "Provides a global point of access to the instance",
      "Allows multiple instances for load balancing",
      "Restricts instantiation of a class to a factory method"
    ],
    correctAnswers: [0, 1],
    level: "medium",
    marks: 2,
    explanation: "The Singleton pattern restricts a class to a single instance and provides global access."
  },
  {
    id: 95,
    text: "What is 'Polymorphism' in object-oriented programming?",
    options: [
      "Objects taking on multiple forms",
      "Encapsulation of data",
      "Inheritance hierarchy",
      "Abstraction of interfaces"
    ],
    correctAnswers: [0],
    level: "medium",
    marks: 2,
    explanation: "Polymorphism allows objects to be treated as instances of their parent class rather than their actual class."
  },
  {
    id: 96,
    text: "What is the role of 'JIT' compiler in .NET?",
    options: [
      "To compile IL code to native machine code at runtime",
      "To interpret code line by line",
      "To compile code during development",
      "To optimize code for performance"
    ],
    correctAnswers: [0],
    level: "experienced",
    marks: 3,
    explanation: "The Just-In-Time compiler converts Intermediate Language (IL) to native code at runtime."
  },
  {
    id: 97,
    text: "Which of the following statements are true about garbage collection in .NET?",
    options: [
      "It frees managed memory",
      "It can be forced by the programmer",
      "It prevents memory leaks from unmanaged resources",
      "It automatically reclaims unused objects"
    ],
    correctAnswers: [0, 1, 3],
    level: "medium",
    marks: 2,
    explanation: "Garbage collection frees managed memory, can be forced using 'GC.Collect()', and reclaims unused objects."
  },
  {
    id: 98,
    text: "What is 'delegation' in the context of design patterns?",
    options: [
      "An object handling a request by delegating to a second object",
      "Inheriting behavior from a base class",
      "Encapsulating a request as an object",
      "Defining an interface for creating an object"
    ],
    correctAnswers: [0],
    level: "experienced",
    marks: 3,
    explanation: "Delegation involves an object relying on another to provide a specified set of functionalities."
  },
  {
    id: 99,
    text: "Which of the following are benefits of using interfaces in C#?",
    options: [
      "Facilitates multiple inheritance",
      "Promotes loose coupling",
      "Enables polymorphism",
      "Improves performance"
    ],
    correctAnswers: [0, 1, 2],
    level: "medium",
    marks: 2,
    explanation: "Interfaces enable multiple inheritance, loose coupling, and polymorphism."
  },
  // Question 100 was previously provided
  {
    id: 100,
    text: "What is the main purpose of the IDisposable interface?",
    options: [
      "To provide a method for releasing unmanaged resources",
      "To enforce garbage collection",
      "To manage memory allocation",
      "To handle exceptions"
    ],
    correctAnswers: [0],
    level: "medium",
    marks: 2,
    explanation: "IDisposable defines a standard method for releasing unmanaged resources."
  },
  {
    id: 101,
    text: "What does the 'readonly' keyword do when applied to a field in C#?",
    options: [
      "Makes the field immutable after initialization",
      "Allows the field to be modified only within the constructor",
      "Prevents the field from being serialized",
      "Makes the field accessible only within its class"
    ],
    correctAnswers: [1],
    level: "medium",
    marks: 2,
    explanation: "A 'readonly' field can only be assigned during declaration or in a constructor."
  },
  {
    id: 102,
    text: "Which of the following are correct ways to create an instance of an anonymous type?",
    options: [
      "var obj = new { Name = \"John\", Age = 30 };",
      "dynamic obj = new { \"John\", 30 };",
      "object obj = new { Name = \"John\", Age = 30 };",
      "var obj = new object { Name = \"John\", Age = 30 };"
    ],
    correctAnswers: [0],
    level: "experienced",
    marks: 3,
    explanation: "Anonymous types are created using 'new { }' with property initializers."
  },
  {
    id: 103,
    text: "What is the purpose of 'String.Join' method in C#?",
    options: [
      "To concatenate elements of a collection into a single string with a separator",
      "To split a string into an array",
      "To compare two strings",
      "To replace characters in a string"
    ],
    correctAnswers: [0],
    level: "easy",
    marks: 1,
    explanation: "'String.Join' concatenates an array or IEnumerable into a single string separated by a specified separator."
  },
  {
    id: 104,
    text: "In C#, what does the 'protected internal' access modifier mean?",
    options: [
      "Accessible within its own assembly or derived classes",
      "Accessible only within its own class",
      "Accessible within the same assembly",
      "Accessible only to derived classes"
    ],
    correctAnswers: [0],
    level: "medium",
    marks: 2,
    explanation: "'protected internal' members are accessible within their own assembly or to derived classes."
  },
  {
    id: 105,
    text: "Which LINQ method would you use to remove duplicate elements from a sequence?",
    options: ["Distinct()", "Union()", "Except()", "Where()"],
    correctAnswers: [0],
    level: "easy",
    marks: 1,
    explanation: "'Distinct()' returns a new sequence without duplicate elements."
  },
  {
    id: 106,
    text: "What is the output of the following code?\n\n```csharp\nint[] numbers = {1, 2, 3, 4};\nvar result = numbers.Aggregate((a, b) => a + b);\nConsole.WriteLine(result);\n```",
    options: ["10", "24", "1234", "Error"],
    correctAnswers: [0],
    level: "medium",
    marks: 2,
    explanation: "'Aggregate' applies an accumulator function over a sequence, summing up to 10."
  },
  {
    id: 107,
    text: "What is the main difference between 'IEnumerable' and 'IEnumerator'?",
    options: [
      "'IEnumerable' represents a collection; 'IEnumerator' enables iteration over the collection",
      "'IEnumerator' represents a collection; 'IEnumerable' enables iteration",
      "They are the same",
      "'IEnumerable' supports modification; 'IEnumerator' does not"
    ],
    correctAnswers: [0],
    level: "medium",
    marks: 2,
    explanation: "'IEnumerable' provides an enumerator; 'IEnumerator' allows iteration over a collection."
  },
  {
    id: 108,
    text: "Which of the following are valid JSON data types that can be deserialized into C# objects?",
    options: ["String", "Number", "Object", "Function"],
    correctAnswers: [0, 1, 2],
    level: "medium",
    marks: 2,
    explanation: "JSON data types include String, Number, Object, Array, Boolean, and null; 'Function' is not valid."
  },
  {
    id: 109,
    text: "In C#, what does the 'params' keyword allow you to do?",
    options: [
      "Pass a variable number of arguments to a method",
      "Specify optional parameters",
      "Pass parameters by reference",
      "Define a method with multiple return types"
    ],
    correctAnswers: [0],
    level: "easy",
    marks: 1,
    explanation: "'params' allows methods to accept a variable number of arguments."
  },
  {
    id: 110,
    text: "What is 'Reflection.Emit' used for in C#?",
    options: [
      "To dynamically create new types at runtime",
      "To inspect metadata",
      "To serialize objects",
      "To handle events"
    ],
    correctAnswers: [0],
    level: "experienced",
    marks: 3,
    explanation: "'Reflection.Emit' allows for the dynamic creation and compilation of new types at runtime."
  },
  {
    id: 111,
    text: "Which keyword is used to overload an operator in C#?",
    options: ["operator", "overload", "override", "extend"],
    correctAnswers: [0],
    level: "medium",
    marks: 2,
    explanation: "The 'operator' keyword is used to declare an operator overload."
  },
  {
    id: 112,
    text: "What is the purpose of the 'checked' keyword in C#?",
    options: [
      "To enable overflow checking for arithmetic operations",
      "To validate user input",
      "To perform type checking",
      "To mark code that has been reviewed"
    ],
    correctAnswers: [0],
    level: "experienced",
    marks: 3,
    explanation: "'checked' ensures that arithmetic operations that exceed the limits of the data type throw an exception."
  },
  {
    id: 113,
    text: "What is 'Late Binding' in C#?",
    options: [
      "Binding that occurs at runtime rather than compile time",
      "Deferring method execution",
      "Delaying object instantiation",
      "Using obsolete methods"
    ],
    correctAnswers: [0],
    level: "experienced",
    marks: 3,
    explanation: "Late Binding refers to method calls that are resolved at runtime."
  },
  {
    id: 114,
    text: "Which of the following statements are true about 'static' constructors?",
    options: [
      "They initialize static members of the class",
      "They cannot have access modifiers",
      "They are called automatically before the first instance is created",
      "They can take parameters"
    ],
    correctAnswers: [0, 1, 2],
    level: "medium",
    marks: 2,
    explanation: "Static constructors initialize static members, cannot have access modifiers or parameters, and are called automatically."
  },
  {
    id: 115,
    text: "What is the output of the following code?\n\n```csharp\nstring s = \"C# Programming\";\nConsole.WriteLine(s.Substring(2, 4));\n```",
    options: ["Prog", "# Pr", "Program", "C# Pr"],
    correctAnswers: [1],
    level: "medium",
    marks: 2,
    explanation: "'Substring(2, 4)' starts at index 2 and takes 4 characters, resulting in '# Pr'."
  },
  {
    id: 116,
    text: "In C#, what is 'Method Hiding'?",
    options: [
      "Using 'new' keyword to hide a base class method",
      "Overriding a base class method",
      "Preventing method overriding",
      "Making a method private"
    ],
    correctAnswers: [0],
    level: "experienced",
    marks: 3,
    explanation: "Method hiding involves using the 'new' keyword to hide an inherited member."
  },
  {
    id: 117,
    text: "What is 'Currying' in programming?",
    options: [
      "Transforming a function with multiple arguments into a sequence of functions each with a single argument",
      "Executing code in a separate thread",
      "Caching the results of a function",
      "Optimizing recursive functions"
    ],
    correctAnswers: [0],
    level: "experienced",
    marks: 3,
    explanation: "Currying is a functional programming technique of translating a function."
  },
  {
    id: 118,
    text: "Which of the following are mutable collections in C#?",
    options: ["Array", "List", "ImmutableArray", "Dictionary"],
    correctAnswers: [0, 1, 3],
    level: "medium",
    marks: 2,
    explanation: "'Array', 'List', and 'Dictionary' are mutable; 'ImmutableArray' is immutable."
  },
  {
    id: 119,
    text: "What does 'Thread.Sleep(0)' do in C#?",
    options: [
      "Causes the thread to yield execution to another thread of equal priority",
      "Suspends the thread indefinitely",
      "Resumes a suspended thread",
      "Throws an exception"
    ],
    correctAnswers: [0],
    level: "experienced",
    marks: 3,
    explanation: "'Thread.Sleep(0)' yields the thread's current time slice to any thread of equal priority."
  },
  {
    id: 120,
    text: "What is the use of the 'volatile' keyword in C#?",
    options: [
      "To indicate that a field may be modified by multiple threads concurrently",
      "To declare a constant value",
      "To prevent a field from being optimized by the compiler",
      "To mark a method for inlining"
    ],
    correctAnswers: [0],
    level: "experienced",
    marks: 3,
    explanation: "'volatile' ensures that the field is always read from and written to main memory."
  },
  {
    id: 121,
    text: "What is 'deadlock' in the context of multithreading?",
    options: [
      "When two threads are blocked forever, each waiting for the other",
      "When a thread goes into an infinite loop",
      "When a thread is terminated unexpectedly",
      "When a thread cannot acquire a lock"
    ],
    correctAnswers: [0],
    level: "medium",
    marks: 2,
    explanation: "Deadlock occurs when two or more threads are waiting indefinitely for each other to release resources."
  },
  {
    id: 122,
    text: "Which of the following are benefits of using 'async' and 'await' keywords?",
    options: [
      "Simplify asynchronous code",
      "Improve application responsiveness",
      "Avoid blocking the main thread",
      "Increase application security"
    ],
    correctAnswers: [0, 1, 2],
    level: "medium",
    marks: 2,
    explanation: "'async' and 'await' make asynchronous programming more straightforward and improve responsiveness."
  },
  {
    id: 123,
    text: "What is 'LINQ to XML' in C#?",
    options: [
      "An API to query XML documents using LINQ syntax",
      "A method to convert LINQ queries into XML",
      "A way to store LINQ queries in XML format",
      "A protocol for transmitting LINQ over networks"
    ],
    correctAnswers: [0],
    level: "experienced",
    marks: 3,
    explanation: "'LINQ to XML' allows querying and modifying XML documents using LINQ syntax."
  },
  {
    id: 124,
    text: "Which exception is thrown when attempting to divide an integer by zero?",
    options: [
      "DivideByZeroException",
      "ArithmeticException",
      "InvalidOperationException",
      "OverflowException"
    ],
    correctAnswers: [0],
    level: "easy",
    marks: 1,
    explanation: "Dividing an integer by zero throws a DivideByZeroException."
  },
  {
    id: 125,
    text: "In C#, which of the following are correct ways to declare a lambda expression?",
    options: [
      "(x) => x * x",
      "delegate(x) { return x * x; }",
      "Func<int, int> square = x => x * x;",
      "(int x) => { return x * x; }"
    ],
    correctAnswers: [0, 2, 3],
    level: "medium",
    marks: 2,
    explanation: "All are valid lambda expressions except the one using 'delegate' keyword, which is an anonymous method."
  },
  {
    id: 126,
    text: "What does the 'dynamic' keyword in C# do?",
    options: [
      "Defers type checking until runtime",
      "Specifies a variable type that can change",
      "Enables late binding",
      "Allows for dynamic method dispatch"
    ],
    correctAnswers: [0, 2, 3],
    level: "experienced",
    marks: 3,
    explanation: "'dynamic' allows for late binding, deferring type checking until runtime, and supports dynamic dispatch."
  },
  {
    id: 127,
    text: "Which of the following are built-in generic delegates in C#?",
    options: ["Func", "Action", "Predicate", "EventHandler"],
    correctAnswers: [0, 1, 2],
    level: "medium",
    marks: 2,
    explanation: "'Func', 'Action', and 'Predicate' are built-in generic delegates; 'EventHandler' is not generic."
  },
  {
    id: 128,
    text: "What is the difference between 'string' and 'String' in C#?",
    options: [
      "'string' is a C# alias for 'System.String'",
      "'String' is a C# alias for 'System.string'",
      "There is no difference",
      "'string' is mutable; 'String' is immutable"
    ],
    correctAnswers: [0, 2],
    level: "easy",
    marks: 1,
    explanation: "'string' is an alias for 'System.String'; they are the same."
  },
  {
    id: 129,
    text: "What is 'Operator Overloading' in C#?",
    options: [
      "Defining custom behavior for standard operators",
      "Using multiple operators in a single expression",
      "Overriding methods from a base class",
      "Creating extension methods for operators"
    ],
    correctAnswers: [0],
    level: "experienced",
    marks: 3,
    explanation: "Operator overloading allows developers to define custom behavior for operators when applied to user-defined types."
  },
  {
    id: 130,
    text: "Which of the following is true about the 'lock' statement in C#?",
    options: [
      "It ensures that one thread doesn't enter a critical section of code while another thread is in it",
      "It locks the CPU to prevent context switching",
      "It can be used to prevent deadlocks",
      "It can only lock reference types"
    ],
    correctAnswers: [0, 3],
    level: "medium",
    marks: 2,
    explanation: "'lock' is used for synchronization and can only lock on reference types."
  },
  {
    id: 131,
    text: "What is 'Tail Recursion'?",
    options: [
      "A recursive function where the recursive call is the last operation",
      "A function that calls itself indirectly",
      "Recursion that uses excessive stack memory",
      "A function that has multiple recursive calls"
    ],
    correctAnswers: [0],
    level: "experienced",
    marks: 3,
    explanation: "In tail recursion, the recursive call is the last thing executed in the function."
  },
  {
    id: 132,
    text: "What does 'GC' stand for in .NET, and what is its purpose?",
    options: [
      "Garbage Collector; manages memory allocation and deallocation",
      "Global Class; base class for all objects",
      "Generic Class; allows for type parameters",
      "Graphics Component; handles rendering"
    ],
    correctAnswers: [0],
    level: "easy",
    marks: 1,
    explanation: "GC stands for Garbage Collector, which automatically manages memory."
  },
  {
    id: 133,
    text: "Which of the following are types of JIT (Just-In-Time) compilation in .NET?",
    options: [
      "Pre-JIT",
      "Econo-JIT",
      "Normal-JIT",
      "Quick-JIT"
    ],
    correctAnswers: [0, 1, 2],
    level: "experienced",
    marks: 3,
    explanation: "The types of JIT are Pre-JIT, Econo-JIT, and Normal-JIT."
  },
  {
    id: 134,
    text: "In C#, what is an 'Event'?",
    options: [
      "A mechanism for communication between objects",
      "A delegate",
      "A type of exception",
      "A static method"
    ],
    correctAnswers: [0, 1],
    level: "medium",
    marks: 2,
    explanation: "An event is a way for a class to notify other classes or objects when something of interest occurs."
  },
  {
    id: 135,
    text: "What is the purpose of the 'is' operator in C#?",
    options: [
      "To check the runtime type of an object",
      "To cast an object to a specific type",
      "To compare two object instances",
      "To check for null values"
    ],
    correctAnswers: [0],
    level: "easy",
    marks: 1,
    explanation: "The 'is' operator checks if an object is of a particular type."
  },
  {
    id: 136,
    text: "Which method is used to register a dependency in ASP.NET Core's dependency injection container?",
    options: ["AddTransient", "AddScoped", "AddSingleton", "AddDependency"],
    correctAnswers: [0, 1, 2],
    level: "medium",
    marks: 2,
    explanation: "Dependencies are registered using 'AddTransient', 'AddScoped', or 'AddSingleton'."
  },
  {
    id: 137,
    text: "What is the default behavior of methods in an interface in C# 8.0 and later?",
    options: [
      "They can have default implementations",
      "They must be abstract",
      "They cannot have any implementation",
      "They can be static"
    ],
    correctAnswers: [0],
    level: "experienced",
    marks: 3,
    explanation: "C# 8.0 introduced default interface methods with implementations."
  },
  {
    id: 138,
    text: "What is 'Asynchronous Programming Model' (APM) in .NET?",
    options: [
      "An older pattern for asynchronous operations using Begin/End methods",
      "A pattern using async and await",
      "A pattern that uses events for async operations",
      "A pattern that uses tasks for async operations"
    ],
    correctAnswers: [0],
    level: "experienced",
    marks: 3,
    explanation: "APM uses BeginMethodName and EndMethodName patterns for async operations."
  },
  {
    id: 139,
    text: "Which of the following are advantages of using 'StringBuilder' over 'String' for concatenation?",
    options: [
      "Better performance for multiple modifications",
      "Lower memory overhead",
      "Immutability",
      "Thread safety"
    ],
    correctAnswers: [0, 1],
    level: "medium",
    marks: 2,
    explanation: "StringBuilder is mutable and optimized for scenarios with frequent modifications."
  },
  {
    id: 140,
    text: "In Entity Framework, what is 'Code First' approach?",
    options: [
      "Creating database schema from model classes",
      "Generating model classes from an existing database",
      "Writing SQL queries directly",
      "Using stored procedures"
    ],
    correctAnswers: [0],
    level: "medium",
    marks: 2,
    explanation: "Code First allows developers to define the model classes first, and EF generates the database schema."
  },
  {
    id: 141,
    text: "What is 'serialization' in the context of .NET?",
    options: [
      "Converting an object into a format that can be stored or transmitted",
      "Executing code in a specific order",
      "Encrypting data",
      "Compiling code"
    ],
    correctAnswers: [0],
    level: "easy",
    marks: 1,
    explanation: "Serialization is the process of converting an object into a form that can be persisted or transported."
  },
  {
    id: 142,
    text: "Which of the following statements are true about 'abstract' classes?",
    options: [
      "They cannot be instantiated",
      "They can contain abstract methods",
      "They can contain non-abstract methods",
      "They must be inherited"
    ],
    correctAnswers: [0, 1, 2, 3],
    level: "medium",
    marks: 2,
    explanation: "Abstract classes cannot be instantiated, can have both abstract and concrete methods, and are meant to be inherited."
  },
  {
    id: 143,
    text: "What is the difference between 'const' and 'readonly' in C#?",
    options: [
      "'const' is evaluated at compile time; 'readonly' at runtime",
      "'const' can only be applied to primitive types; 'readonly' can be any type",
      "'readonly' fields can be assigned in constructors; 'const' cannot",
      "All of the above"
    ],
    correctAnswers: [3],
    level: "experienced",
    marks: 3,
    explanation: "All statements are true about the differences between 'const' and 'readonly'."
  },
  {
    id: 144,
    text: "Which of the following are methods of the 'Thread' class in C#?",
    options: ["Start()", "Abort()", "Sleep()", "Join()"],
    correctAnswers: [0, 1, 2, 3],
    level: "medium",
    marks: 2,
    explanation: "All listed methods are members of the 'Thread' class."
  },
  {
    id: 145,
    text: "What is 'Method Overriding' in C#?",
    options: [
      "Redefining a base class method in a derived class",
      "Defining multiple methods with the same name",
      "Hiding a method using the 'new' keyword",
      "Implementing an interface method"
    ],
    correctAnswers: [0],
    level: "medium",
    marks: 2,
    explanation: "Method overriding allows a derived class to provide a specific implementation of a method declared in its base class."
  },
  {
    id: 146,
    text: "What is the purpose of 'unsafe' code in C#?",
    options: [
      "To allow the use of pointers and direct memory manipulation",
      "To disable type safety",
      "To execute code with elevated permissions",
      "To mark code that should not be optimized"
    ],
    correctAnswers: [0],
    level: "experienced",
    marks: 3,
    explanation: "'unsafe' code allows the use of pointers and unmanaged code."
  },
  {
    id: 147,
    text: "Which of the following are value types in C#?",
    options: ["enum", "struct", "decimal", "interface"],
    correctAnswers: [0, 1, 2],
    level: "easy",
    marks: 1,
    explanation: "'enum', 'struct', and 'decimal' are value types; 'interface' is a reference type."
  },
  {
    id: 148,
    text: "What is 'Asynchronous Programming' primarily used for?",
    options: [
      "Improving application responsiveness",
      "Handling multiple tasks simultaneously",
      "Simplifying code",
      "Enhancing security"
    ],
    correctAnswers: [0, 1],
    level: "medium",
    marks: 2,
    explanation: "Asynchronous programming allows applications to handle multiple tasks without blocking."
  },
  {
    id: 149,
    text: "What does the 'out' keyword signify when used in method parameters?",
    options: [
      "The parameter is passed by reference and must be assigned before the method returns",
      "The parameter is optional",
      "The parameter is an output parameter",
      "The parameter cannot be modified"
    ],
    correctAnswers: [0, 2],
    level: "medium",
    marks: 2,
    explanation: "'out' parameters are passed by reference and are used to return multiple values."
  },
  {
    id: 150,
    text: "Which of the following are characteristics of the 'Facade' design pattern?",
    options: [
      "Provides a simplified interface to a complex subsystem",
      "Improves code readability",
      "Allows for loose coupling",
      "Increases subsystem complexity"
    ],
    correctAnswers: [0, 1, 2],
    level: "experienced",
    marks: 3,
    explanation: "The Facade pattern simplifies interaction with complex subsystems and promotes loose coupling."
  }
];
